/**
 * BrowserPushToggle — per-device Web Push subscription switch (this browser
 * only). This is NOT a policy override: the admin notification policy still
 * decides what is delivered. Hidden when the browser lacks PushManager;
 * reports "not configured" when the server has no VAPID keys.
 */

import { useEffect, useState } from 'react';
import { BellRing, BellOff } from 'lucide-react';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { clsx } from 'clsx';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

type State = 'loading' | 'unsupported' | 'unconfigured' | 'denied' | 'off' | 'on';

export function BrowserPushToggle() {
  const [state, setState] = useState<State>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setState('unsupported'); return; }
      try {
        const res = await fetch('/v1/notifications/browser-push/vapid-key', {
          headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
        });
        if (!res.ok) { setState('unconfigured'); return; }
        const body = await res.json() as { ok: true; data: { publicKey: string | null } };
        if (!body.data.publicKey) { setState('unconfigured'); return; }
        if (Notification.permission === 'denied') { setState('denied'); return; }
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager.getSubscription();
        if (!sub) { setState('off'); return; }
        const statusRes = await fetch(`/v1/notifications/browser-push/subscription?endpoint=${encodeURIComponent(sub.endpoint)}`, {
          headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
        });
        if (!statusRes.ok) throw new Error('Could not verify this browser subscription');
        const status = await statusRes.json() as { ok: true; data: { subscribed: boolean } };
        setState(status.data.subscribed ? 'on' : 'off');
      } catch {
        setState('unconfigured');
      }
    })();
  }, []);

  async function subscribe() {
    setBusy(true);
    setError(null);
    try {
      const keyRes = await fetch('/v1/notifications/browser-push/vapid-key', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      const { data } = await keyRes.json() as { data: { publicKey: string | null } };
      if (!data.publicKey) { setState('unconfigured'); return; }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { setState(permission === 'denied' ? 'denied' : 'off'); return; }
      const reg = (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.register('/sw.js'));
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey) as BufferSource,
      });
      const json = sub.toJSON();
      const res = await fetch('/v1/notifications/browser-push/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({
          endpoint: sub.endpoint,
          keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
          userAgent: navigator.userAgent.slice(0, 255),
        }),
      });
      if (!res.ok) throw new Error(`Subscribe failed (${res.status})`);
      setState('on');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Subscribe failed');
    } finally {
      setBusy(false);
    }
  }

  async function unsubscribe() {
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        const res = await fetch('/v1/notifications/browser-push/subscription', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        if (!res.ok) throw new Error(`Unsubscribe failed (${res.status})`);
        await sub.unsubscribe();
      }
      setState('off');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unsubscribe failed');
    } finally {
      setBusy(false);
    }
  }

  if (state === 'unsupported') return null;

  return (
    <div className="bg-bg-panel border border-border rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
      <div className="flex items-center gap-2 min-w-0">
        {state === 'on' ? <BellRing size={15} className="text-emerald-300 shrink-0" /> : <BellOff size={15} className="text-muted shrink-0" />}
        <div className="min-w-0">
          <div className="text-sm text-white">Browser push on this device</div>
          <div className="text-[11px] text-muted">
            {state === 'unconfigured'
              ? 'Not configured on the server yet'
              : state === 'denied'
                ? 'Blocked by the browser — allow notifications in the browser site settings'
                : state === 'on'
                  ? 'This browser receives push notifications (admin policy still applies)'
                  : 'This browser does not receive push notifications'}
          </div>
        </div>
      </div>
      {(state === 'off' || state === 'on') && (
        <button
          onClick={() => void (state === 'on' ? unsubscribe() : subscribe())}
          disabled={busy}
          className={clsx(
            'sm:ml-auto text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50 w-full sm:w-auto',
            state === 'on'
              ? 'bg-bg-card border-border text-muted hover:text-white hover:bg-bg-hover'
              : 'bg-accent/20 border-accent text-emerald-300 hover:bg-accent/30',
          )}
        >
          {busy ? '…' : state === 'on' ? 'Turn off' : 'Turn on'}
        </button>
      )}
      {error && <div className="text-[11px] text-rose-400">{error}</div>}
    </div>
  );
}
